import React, {useReducer} from 'react';
import {Context} from './Context';
import {initialState, reducer} from './Reducer';
import {IContext, ACTION_VERBS} from './Types';
import useContextState from '../user/Context';
import * as apiService from '../../services';
import {
  IBalanceSheetResponse,
  IDebtorListResponse,
  IProductListResponse,
} from '../../interfaces/services';
import {
  IBalanceSheetItem,
  IDebtorItem,
  IFilterTracking,
  ISemaphoreValue,
} from '../../interfaces/dtos';
import {handleError} from '../../utils/exceptions/handle-error';
import {
  ICategory,
  IListOption,
  IPortfolioFilter,
  IProduct,
} from '../commons/interfaces';
import {ISalesmanResourceResponse} from '../../interfaces/services/salesman-resources.interface';
import {ISemaphoreResponse} from '../../interfaces/services/semaphore-response.interface';

/**
 *
 * @param props
 * @returns
 */
export const ClientProvider = (props: any) => {
  const {
    CLEAR_ERRORS,
    STATE_PROCESS_ERROR,
    GET_SALESMAN_PRODUCTS,
    BALANCE_SHEET,
    SALESMAN_RESOURCES,
    SEMAPHORE_ITEMS,
    SET_PORTFOLIO_FILTER,
    SET_FILTER_TRACKING,
    DEBTOR_LIST,
  } = ACTION_VERBS;

  const {getToken} = useContextState();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getSemaphoreItems = (
    response: ISemaphoreResponse,
  ): ISemaphoreValue[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getTerritoriesAtLogin = (payload: any): IListOption[] => {
    if (payload.salesTerritories) {
      return payload.salesTerritories as IListOption[];
    }
    return [];
  };

  const extractCustomerTypes = (payload: any): IListOption[] => {
    if (payload.customerTypes) {
      return payload.customerTypes as IListOption[];
    }
    return [];
  };

  const extractRequiredDocumentList = (payload: any): IListOption[] => {
    if (payload.requiredDocumentList) {
      return payload.requiredDocumentList as IListOption[];
    }
    return [];
  };

  const extractPaymentOptions = (payload: any): IListOption[] => {
    if (payload.paymentOptions) {
      return payload.paymentOptions as IListOption[];
    }
    return [];
  };

  const extractCategories = (payload: any): ICategory[] => {
    if (payload.salesCategories) {
      return payload.salesCategories as ICategory[];
    }
    return [];
  };

  const extractCities = (payload: any): IListOption[] => {
    if (payload.salesCities) {
      return payload.salesCities as IListOption[];
    }
    return [];
  };

  const extracRequiredDocumentsHelp = (payload: any): string => {
    if (payload.requiredDocumentsHelp) {
      return payload.requiredDocumentsHelp;
    }
    return '';
  };

  const parseDebtorList = (response: IDebtorListResponse): IDebtorItem[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const getBalanceSheet = (
    response: IBalanceSheetResponse,
  ): IBalanceSheetItem | null => {
    if (response.payload) {
      return response.payload[0];
    }
    return null;
  };

  const getProductList = (response: IProductListResponse): IProduct[] => {
    if (response.payload) {
      return response.payload;
    }
    return [];
  };

  const fetchClientProducts = async (): Promise<boolean> => {
    try {
      const response: IProductListResponse = await apiService.getClientProducts(
        getToken(),
      );

      if (response.success) {
        const products = getProductList(response);
        dispatch({
          type: GET_SALESMAN_PRODUCTS,
          payload: products,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, GET_SALESMAN_PRODUCTS);
    }
    return false;
  };

  /**
   *
   */
  const clearErrors = () => {
    dispatch({
      type: CLEAR_ERRORS,
    });
  };

  /**
   *
   * @returns
   */
  const fetchBalanceSheet = async (
    filter?: IPortfolioFilter | null,
  ): Promise<boolean> => {
    try {
      const response: IBalanceSheetResponse =
        await apiService.getClientBalanceSheet(getToken(), filter);
      if (response.success) {
        const balance = getBalanceSheet(response);
        dispatch({
          type: BALANCE_SHEET,
          payload: balance,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, BALANCE_SHEET);
    }
    return false;
  };

  const fetchResources = async (): Promise<boolean> => {
    try {
      const response: ISalesmanResourceResponse =
        await apiService.getClientResources(getToken());

      if (response.success) {
        const salesTerritories = getTerritoriesAtLogin(response.payload);

        const customerTypes = extractCustomerTypes(response.payload);

        const customerRequiredDocumentList = extractRequiredDocumentList(
          response.payload,
        );

        const customerRequiredDocumentsHelp = extracRequiredDocumentsHelp(
          response.payload,
        );

        const customerPaymentOptions = extractPaymentOptions(response.payload);

        const salesCategories = extractCategories(response.payload);
        const salesCities = extractCities(response.payload);

        const payload: any = {
          salesTerritories,
          customerTypes,
          customerRequiredDocumentList,
          customerPaymentOptions,
          customerRequiredDocumentsHelp,
          salesCategories,
          salesCities,
        };

        dispatch({
          type: SALESMAN_RESOURCES,
          payload,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SALESMAN_RESOURCES);
    }
    return false;
  };

  const fetchDebtorList = async (
    key: string,
    filter?: IPortfolioFilter | null,
  ): Promise<boolean> => {
    try {
      const response: IDebtorListResponse =
        await apiService.getClientDebtorsList(key, getToken(), filter);

      if (response.success) {
        const documents = parseDebtorList(response);
        dispatch({
          type: DEBTOR_LIST,
          payload: documents,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, DEBTOR_LIST);
    }
    return false;
  };

  const fetchSemaphoreItems = async (
    filters?: IPortfolioFilter | null,
  ): Promise<boolean> => {
    try {
      const response: ISemaphoreResponse =
        await apiService.getClientSemphoreItems(getToken(), filters);

      if (response.success) {
        const semaphoreItems = getSemaphoreItems(response);
        dispatch({
          type: SEMAPHORE_ITEMS,
          payload: semaphoreItems,
        });
        return true;
      }
    } catch (error) {
      handleError(error, STATE_PROCESS_ERROR, SEMAPHORE_ITEMS);
    }
    return false;
  };

  const setClientPortfolioFilter = (filter: IPortfolioFilter | null) => {
    dispatch({
      type: SET_PORTFOLIO_FILTER,
      payload: filter,
    });
  };

  const setFilterTracking = (filter: IFilterTracking) => {
    dispatch({
      type: SET_FILTER_TRACKING,
      payload: filter,
    });
  };

  const value: IContext = {
    ...state,
    clearErrors,
    fetchClientProducts,
    fetchBalanceSheet,
    fetchResources,
    fetchSemaphoreItems,
    setClientPortfolioFilter,
    setFilterTracking,
    fetchDebtorList,
    actions: ACTION_VERBS,
  } as IContext;

  /**
   *
   */
  return <Context.Provider value={value}>{props.children}</Context.Provider>;
};
